/* The primitive set. Everything here is domain-blind: it takes strings, numbers
   and tones, and knows nothing about devices, suppliers or credentials. That is
   the line §13 draws, and it is the reason this set can be lifted into @hird/ui
   without dragging Ledger with it. */

import {
  Fragment,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { useDragScroll } from '../../hooks/useDragScroll';
import { useFillScreen } from '../../hooks/useFillScreen';
import type { Tone } from '../../models';
import s from './ui.module.scss';

export { ToastProvider, useToast } from './Toast';
export type { ToastHandle, ToastKind } from './Toast';

/* ─────────────────────────── Panel ─────────────────────────── */

export function Panel({
  title,
  sub,
  actions,
  flush,
  children,
}: {
  title?: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={s.panel}>
      {(title || actions) && (
        <header className={s.panelHead}>
          <div className={s.growMin}>
            {title && <h2>{title}</h2>}
            {sub && <div className={s.panelSub}>{sub}</div>}
          </div>
          {actions}
        </header>
      )}
      <div className={flush ? s.panelBodyFlush : s.panelBody}>{children}</div>
    </section>
  );
}

/* ─────────────────────────── Badge ─────────────────────────── */

export function Badge({
  tone = 'muted',
  icon,
  title,
  children,
}: {
  tone?: Tone;
  icon?: string;
  /** Hover text. A badge is a compressed label, and some of them compress
      something that needs a sentence — 'DMARC p=none' being the example that
      asked for this. */
  title?: string;
  children: ReactNode;
}) {
  return (
    <span className={`${s.badge} ${s[`tone-${tone}`]}`} title={title}>
      {icon && <i className={`ti ${icon}`} aria-hidden />}
      {children}
    </span>
  );
}

/* ─────────────────────────── StatCard ─────────────────────────── */

export function StatCard({
  label,
  value,
  foot,
  tone = 'ok',
  icon,
  to,
}: {
  label: string;
  value: ReactNode;
  /** The pill on the top row, beside the icon.
   *
   *  Usually two or three words. It used to have to be: the pill was positioned
   *  over the card at 55% of its width, so "Nothing in them — candidates to
   *  retire" wrapped into a narrow strip pressed against the icon. It now sits
   *  in the layout and the card makes room, so a longer one is untidy at worst
   *  rather than broken. */
  foot?: ReactNode;
  tone?: Tone;
  icon?: string;
  to?: string;
}) {
  const body = (
    <>
      {/* One row, so the two things at the top of a card are laid out against
          each other rather than both measured from the corner. */}
      <span className={s.statHead}>
        {icon && (
          <span className={s.statIcon}>
            <i className={`ti ${icon}`} aria-hidden />
          </span>
        )}
        {foot && <span className={`${s.statDelta} ${s[`tone-${tone}`]}`}>{foot}</span>}
      </span>
      <b className={s.statValue}>{value}</b>
      <span className={s.statLabel}>{label}</span>
    </>
  );

  if (to) {
    return (
      <Link to={to} className={`${s.stat} ${s.statLink}`}>
        {body}
      </Link>
    );
  }
  return <div className={s.stat}>{body}</div>;
}

export function StatGrid({ children }: { children: ReactNode }) {
  return <div className={s.statGrid}>{children}</div>;
}

/* ─────────────────────────── Meter ─────────────────────────── */

export function Meter({ value, max, tone = 'info' }: { value: number; max: number; tone?: Tone }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  /* Gradients, not flats — the prototype's `.meter>span`. */
  const colour =
    tone === 'warn'
      ? 'linear-gradient(90deg,#e0992f,#f0b757)'
      : tone === 'bad'
        ? 'linear-gradient(90deg,#c0392b,#e05f4f)'
        : tone === 'ok'
          ? 'linear-gradient(90deg,#2f9e6b,#5cc394)'
          : 'linear-gradient(90deg,var(--primary),#4D9BE0)';
  return (
    <div
      className={s.meter}
      role="meter"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={`${value} of ${max}`}
    >
      <div className={s.meterFill} style={{ width: `${pct}%`, background: colour }} />
    </div>
  );
}

/* ─────────────────────────── DataTable ─────────────────────────── */

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Cell content. */
  render: (row: T) => ReactNode;
  /** Value to sort on. Omit to make the column unsortable. */
  sortBy?: (row: T) => string | number;
  width?: string;
  /** How hard this column fights for space.
   *
   *  1 is always shown, 3 is the first to go and 2 follows it. Default 1, so a
   *  table that has not thought about it behaves as it always did.
   *
   *  The widths are the table's own, not the window's — see the container
   *  queries in ui.module.scss. That distinction is the whole point: the table
   *  gets the window less the rail and the padding, and less another 620px
   *  again while a record is open, so a window measurement was wrong by three
   *  hundred pixels on a good day and by nine hundred on a normal one.
   *
   *  Nothing is hidden for good: every row opens a record that carries the
   *  lot, and what is on screen can still be panned by hand. */
  priority?: 1 | 2 | 3;
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  selectedKey,
  checked,
  onCheckedChange,
  initialSort,
  empty,
  fill = true,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** The row whose record is open. Marked, because in a master–detail view the
      list stays on screen and "which one am I reading about" has to be
      answerable without closing the panel to find out. */
  selectedKey?: string;
  /** Ticked rows, for a bulk action. Opt-in: pass both and a checkbox column
      appears, omit them and the table is exactly what it was. Every other
      register keeps a clean table rather than carrying a column for a feature
      it does not offer. */
  checked?: Set<string>;
  onCheckedChange?: (next: Set<string>) => void;
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  empty?: ReactNode;
  /** Fit the rest of the screen and scroll inside itself, so the column headers stay in
      view and the filters above never scroll away. On by default; turn it off for a
      table sitting inside something that already scrolls and has more below it. */
  fill?: boolean;
}) {
  const [sort, setSort] = useState(initialSort ?? null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortBy) return rows;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = col.sortBy!(a);
      const bv = col.sortBy!(b);
      if (av === bv) return 0;
      return av > bv ? dir : -dir;
    });
  }, [rows, columns, sort]);

  /* Grab-and-swipe, so reaching a column off the right does not mean scrolling
     two hundred rows down to find the scrollbar. */
  const pan = useDragScroll<HTMLDivElement>();
  useFillScreen(pan.ref, fill);

  const selectable = checked !== undefined && onCheckedChange !== undefined;
  /* Against the rows on screen, not the whole register. The header box ticks
     what you are looking at, which is the point of filtering first — narrow to
     Unclassified, tick the lot, act. Ticking eighty rows you cannot see would
     be a different and much more alarming feature. */
  const allShown = selectable && rows.length > 0 && rows.every((r) => checked!.has(rowKey(r)));

  const toggle = (key: string) => {
    const next = new Set(checked);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onCheckedChange!(next);
  };

  if (rows.length === 0 && empty) return <>{empty}</>;

  return (
    <div
      ref={pan.ref}
      className={[s.tableWrap, pan.pannable ? s.pannable : '', pan.panning ? s.panning : '']
        .filter(Boolean)
        .join(' ')}
    >
      <table className={`${s.table} ${selectable ? s.hasCheck : ''}`}>
        <thead>
          <tr>
            {selectable && (
              <th className={s.checkCell}>
                <input
                  type="checkbox"
                  checked={allShown}
                  aria-label={allShown ? 'Clear selection' : `Select all ${rows.length} shown`}
                  onChange={() => {
                    const next = new Set(checked);
                    for (const r of rows) {
                      if (allShown) next.delete(rowKey(r));
                      else next.add(rowKey(r));
                    }
                    onCheckedChange!(next);
                  }}
                />
              </th>
            )}
            {columns.map((c, i) => (
              <th
                key={c.key}
                style={c.width ? { width: c.width } : undefined}
                className={`${c.sortBy ? s.sortable : ''} ${sort?.key === c.key ? s.sorted : ''} ${
                  c.priority === 3 ? s.prio3 : c.priority === 2 ? s.prio2 : ''
                } ${i === 0 ? s.frozen : ''}`}
                onClick={
                  c.sortBy
                    ? () =>
                        setSort((prev) =>
                          prev?.key === c.key
                            ? { key: c.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                            : { key: c.key, dir: 'asc' },
                        )
                    : undefined
                }
                aria-sort={
                  sort?.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined
                }
              >
                {c.header}
                {c.sortBy && (
                  <i
                    className={`ti ${
                      sort?.key === c.key
                        ? sort.dir === 'asc'
                          ? 'ti-arrow-narrow-up'
                          : 'ti-arrow-narrow-down'
                        : 'ti-arrows-sort'
                    }`}
                    aria-hidden
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr
              key={rowKey(row)}
              className={[
                onRowClick ? s.rowClickable : '',
                selectedKey !== undefined && rowKey(row) === selectedKey ? s.rowSelected : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-selected={selectedKey !== undefined ? rowKey(row) === selectedKey : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {selectable && (
                <td className={s.checkCell} onClick={(e) => e.stopPropagation()}>
                  {/* Stops the click opening the record. Ticking a box and
                      having a panel slide over the list is not what anybody
                      meant by it. */}
                  <input
                    type="checkbox"
                    checked={checked!.has(rowKey(row))}
                    aria-label="Select row"
                    onChange={() => toggle(rowKey(row))}
                  />
                </td>
              )}
              {columns.map((c, i) => (
                <td
                  key={c.key}
                  className={[
                    c.priority === 3 ? s.prio3 : c.priority === 2 ? s.prio2 : '',
                    /* The first column is the one you read to know which row you
                       are on, so it is the one that must not slide away. */
                    i === 0 ? s.frozen : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────── EmptyState ─────────────────────────── */

export function EmptyState({ icon = 'ti-inbox', title, children }: { icon?: string; title: string; children?: ReactNode }) {
  return (
    <div className={s.empty}>
      <div className={s.emptyIcon}>
        <i className={`ti ${icon}`} aria-hidden />
      </div>
      <b className={s.emptyTitle}>{title}</b>
      {children && <span className={s.emptyBody}>{children}</span>}
    </div>
  );
}

/* ─────────────────────────── Load failure ───────────────────────────

   A failed read must never be drawn as an empty register.

   TanStack Query leaves data undefined when a query rejects, so a screen that
   only branches on isLoading falls straight through to its empty state and a
   Graph 403 becomes "no devices". That is the failure this whole app exists to
   avoid — a register believed to be accurate and silently is not — so every
   screen that reads checks for this before rendering rows.

   403 is called out by name because it is the likely one and the least
   self-explanatory: the app role got you in, but the SharePoint permission is
   separate and is where reads are actually authorised. */

export function LoadError({ error, what }: { error: unknown; what: string }) {
  const status =
    typeof error === 'object' && error !== null && 'status' in error
      ? (error as { status?: number }).status
      : undefined;

  return (
    <div className={s.empty}>
      <div className={s.emptyIcon} style={{ background: 'var(--bad-soft)', color: 'var(--bad)' }}>
        <i className="ti ti-alert-triangle" aria-hidden />
      </div>
      <b className={s.emptyTitle}>Could not load {what}</b>
      <span className={s.emptyBody}>
        {status === 403 ? (
          <>
            SharePoint refused the read (403). Your sign-in worked, so this is a list
            permission rather than the app registration — check you are in the SharePoint
            group for this site, not just the Entra group.
          </>
        ) : status === 404 ? (
          <>
            The list was not found (404). The site may not be provisioned, or VITE_SITE_ID
            may be pointing at the wrong site.
          </>
        ) : (
          <>
            {error instanceof Error ? error.message : 'The request failed.'} This is a failed
            read, not an empty list — the difference matters, so nothing is shown.
          </>
        )}
      </span>
    </div>
  );
}

/* ─────────────────────────── Drawer ─────────────────────────── */

/* Drawers nest. The department picker opens one over the person panel, and
   two things here are global and were written as if only one could ever be
   open: the layout flag on <html>, and the Escape listener on `document`.
   The inner drawer's cleanup deleted the flag outright, collapsing the page
   back under a drawer that was still open; and both Escape listeners fired,
   so one key press closed the picker AND threw away the record behind it.

   Which one is "on top" is taken from context, not from the order they
   registered in. React runs a child's effects BEFORE its parent's, so a stack
   ordered by when each drawer mounted puts the OUTER one on top — exactly
   backwards, and it passes any test that only ever opens one. */
const DrawerDepthContext = createContext(0);

interface OpenDrawer {
  token: object;
  depth: number;
}

const drawerStack: OpenDrawer[] = [];

function pushDrawer(token: object, depth: number): void {
  drawerStack.push({ token, depth });
  document.documentElement.dataset.recordOpen = 'true';
}

function popDrawer(token: object): void {
  const at = drawerStack.findIndex((d) => d.token === token);
  if (at >= 0) drawerStack.splice(at, 1);
  if (!drawerStack.length) delete document.documentElement.dataset.recordOpen;
}

function isTopDrawer(token: object): boolean {
  /* Deepest wins, and the last to arrive wins a tie — two drawers at the same
     depth are siblings, and the one opened most recently is the one in front. */
  let top: OpenDrawer | null = null;
  for (const d of drawerStack) if (!top || d.depth >= top.depth) top = d;
  return !!top && top.token === token;
}

export function Drawer({
  open,
  title,
  sub,
  avatar,
  meta,
  footer,
  onClose,
  children,
}: {
  open: boolean;
  title: ReactNode;
  sub?: ReactNode;
  /** Initials or an icon for the 56px tile, as in the prototype's `.dr-av`. */
  avatar?: ReactNode;
  /** Badges under the title. */
  meta?: ReactNode;
  /** Pinned below the scrolling body. Save on a seventeen-field form was at the
      bottom of seventeen fields, which is a scroll to reach and a scroll back if
      you then want to change something. */
  footer?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  /* Identity for the stack below, stable for the life of this drawer, and how
     deeply this one is nested inside others. */
  const token = useMemo(() => ({}), []);
  const depth = useContext(DrawerDepthContext) + 1;

  /* Master–detail rather than modal.
   *
   * This was a dialog: aria-modal, a scrim dimming everything, the app vanishing
   * behind it. Modal is right for an interruption — a confirmation, a form you
   * must finish. Reading a record is not an interruption, it is the same task one
   * level deeper, and treating it as a dialog meant the row you clicked was
   * hidden by the panel describing it. That is what made it feel bolted on.
   *
   * So on a wide screen the panel docks and the page makes room for it: the
   * table stays live, the selected row stays highlighted, and clicking another
   * row swaps the panel instead of closing and reopening it. The attribute on
   * <html> is what lets the layout know, since the panel and the page are in
   * different CSS modules and cannot see each other's class names.
   */
  /* Focus belongs to opening, and to nothing else.
   *
   * This ran on every render, because onClose is a fresh arrow function in all
   * fourteen callers — `onClose={() => setEditing(false)}` — so the effect's
   * dependency changed identity each time and React tore it down and set it up
   * again. Setting up meant ref.current.focus(), which pulled the caret out of
   * whatever input was being typed in: one letter, then click back into the box,
   * then one more. Every drawer in the app, every form.
   *
   * The layout flag moved here for the same reason. Deleting an attribute off
   * <html> and immediately putting it back, on every keystroke, is a style
   * recalculation of the whole page for no reason at all. */
  useEffect(() => {
    if (!open) return undefined;
    ref.current?.focus();
    pushDrawer(token, depth);
    return () => popDrawer(token);
  }, [open, token, depth]);

  /* Escape stays keyed to onClose, and re-binding a listener is harmless — it
     is only the focus call above that could not survive being run twice. */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      /* Only the drawer on top answers. Both listeners are on `document`, so
         before the stack existed one Escape closed the department picker AND
         the person behind it — losing the record you were working on to a key
         you pressed to dismiss a checkbox list. */
      if (e.key === 'Escape' && isTopDrawer(token)) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, token]);

  if (!open) return null;

  return (
    <>
      {/* Still there on a narrow screen, where the panel has to cover the page
          because there is no room beside it. Never on a wide one. */}
      <div className={s.scrim} onClick={onClose} />
      <div
        className={s.drawer}
        role="dialog"
        aria-label="Record"
        tabIndex={-1}
        ref={ref}
      >
        <header className={s.drawerHead}>
          {avatar && <span className={s.drawerAvatar}>{avatar}</span>}
          <div className={s.drawerTitle}>
            <h2>{title}</h2>
            {sub && <div className={s.panelSub}>{sub}</div>}
            {meta && <div className={s.drawerMeta}>{meta}</div>}
          </div>
          <button type="button" className={s.closeBtn} onClick={onClose} aria-label="Close">
            <i className="ti ti-x" aria-hidden />
          </button>
        </header>
        {/* Anything opening a drawer from in here is one level deeper. */}
        <div className={s.drawerBody}>
          <DrawerDepthContext.Provider value={depth}>{children}</DrawerDepthContext.Provider>
        </div>
        {footer && <div className={s.drawerFooter}>{footer}</div>}
      </div>
    </>
  );
}

/* ─────────────────────────── KeyValue ─────────────────────────── */

export interface KeyValueRow {
  key: string;
  value: ReactNode;
  /** Marks a field Ledger owns, as opposed to one the sync overwrites (§10). */
  owned?: boolean;
  /** Heading this row sits under. Consecutive rows sharing one are grouped, and
      a list with none renders exactly as it always did. */
  section?: string;
}

/* Rows, not cells.
 *
 * This was a bordered grid of equal boxes — auto-fit at 190px, so a wide drawer
 * got three columns of them. It read as a table, and it gave every fact the same
 * weight: a person's email and the day their retention expires drawn identically,
 * thirteen times over. Nothing was findable because nothing was subordinate.
 *
 * A plain label/value row with the labels aligned scans in one pass, and a
 * section heading does the grouping the boxes were pretending to do. It also
 * cannot fall apart at narrow widths, which the grid did twice.
 */
export function KeyValue({ rows }: { rows: KeyValueRow[] }) {
  const shown = rows.filter((r) => r.value !== undefined && r.value !== null);

  return (
    <dl className={s.kv}>
      {shown.map((r, i) => (
        <Fragment key={r.key}>
          {/* Only when it changes, so a caller can label every row without
              repeating the heading down the whole list. */}
          {r.section && r.section !== shown[i - 1]?.section && (
            <dt className={s.kvSection}>{r.section}</dt>
          )}
          <div className={`${s.kvRow} ${r.owned ? s.kvOwned : ''}`}>
            <dt className={s.kvKey}>{r.key}</dt>
            <dd className={s.kvValue}>{r.value || '—'}</dd>
          </div>
        </Fragment>
      ))}
    </dl>
  );
}

/* ─────────────────────────── Section ─────────────────────────── */

/* A foldable block, for drawers that outgrew one screen.
 *
 * Built on <details> rather than a useState toggle: keyboard operation, screen
 * reader semantics and open/closed state all come for free and correctly, which
 * a div with an onClick has to reimplement and usually gets half right.
 *
 * The count is on the summary on purpose. A collapsed section that says nothing
 * about what is inside it is a question rather than an answer — "Groups 4" tells
 * you whether opening it is worth the click, and that is the entire job of a
 * closed section.
 */
export function Section({
  title,
  count,
  defaultOpen = false,
  children,
}: {
  title: ReactNode;
  /** Shown on the summary. 0 renders, because zero is an answer. */
  count?: number;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  return (
    <details className={s.section} open={defaultOpen}>
      <summary className={s.sectionSummary}>
        <i className={`ti ti-chevron-right ${s.sectionChevron}`} aria-hidden />
        <span className={s.sectionTitle}>{title}</span>
        {count !== undefined && <span className={s.sectionCount}>{count}</span>}
      </summary>
      <div className={s.sectionBody}>{children}</div>
    </details>
  );
}

/* The first few, then the rest on request.
 *
 * A group's member list is the case that forced this: SG-All-Staff has every
 * account in the company in it, and rendering all of them put nine thousand
 * pixels of names inside a drawer six hundred wide. Nobody reads the hundred and
 * eightieth row, but everybody scrolls past it.
 */
export function Capped({
  cap = 6,
  children,
  noun = 'all',
}: {
  cap?: number;
  children: ReactNode;
  /** Completes "Show all 180 —" so the button says what it will show. */
  noun?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const items = Array.isArray(children) ? children.flat() : [children];
  if (items.length <= cap) return <>{items}</>;

  return (
    <>
      {expanded ? items : items.slice(0, cap)}
      <button type="button" className={s.showAll} onClick={() => setExpanded(!expanded)}>
        {expanded ? 'Show fewer' : `Show ${noun} ${items.length}`}
        <i className={`ti ti-chevron-${expanded ? 'up' : 'down'}`} aria-hidden />
      </button>
    </>
  );
}

/* ─────────────────────────── Record summary ─────────────────────────── */

/* The answer before the reading.
 *
 * A record panel that opens straight into eleven label/value rows makes you
 * assemble the state yourself — is this person active, are they protected, are
 * they costing anything, are they holding kit. Every device-management tool
 * worth copying leads with three or four of those instead, and the properties
 * are what you consult afterwards to find out why.
 */
export function SummaryTiles({ children, large = false }: { children: ReactNode; large?: boolean }) {
  return <div className={`${s.summary} ${large ? s.summaryLarge : ''}`}>{children}</div>;
}

export function SummaryTile({
  icon,
  value,
  label,
  tone = 'muted',
  title,
  onClick,
  active = false,
}: {
  icon?: string;
  value: ReactNode;
  label: string;
  tone?: Tone;
  title?: string;
  /** Makes the tile a button, for tiles that are also the filter for the list beneath them. */
  onClick?: () => void;
  /** Which filter is showing, so the tile that is the current list reads as selected. */
  active?: boolean;
}) {
  const inner = (
    <>
      <div className={s.summaryValue}>
        {icon && <i className={`ti ${icon}`} aria-hidden />}
        <span>{value}</span>
      </div>
      <div className={s.summaryLabel}>{label}</div>
    </>
  );
  const cls = `${s.summaryTile} ${s[`tone-${tone}`]}`;
  if (!onClick) {
    return <div className={cls} title={title}>{inner}</div>;
  }
  return (
    <button
      type="button"
      className={`${cls} ${s.summaryButton} ${active ? s.summaryActive : ''}`}
      title={title}
      aria-pressed={active}
      onClick={onClick}
    >
      {inner}
    </button>
  );
}

/* A link to other records, drawn as one.
 *
 * Properties and relationships were rendered identically — "Email" and
 * "Licences (7)" as the same kind of section in the same vertical stream — and
 * they are not the same kind of thing. One is a fact about this record; the
 * other is a pointer at seven others. Giving them one border, an icon and a
 * count is what makes that legible without reading a word.
 */
export function RelationCard({
  icon,
  title,
  count,
  preview,
  defaultOpen = false,
  children,
}: {
  icon: string;
  title: string;
  count: number;
  /** The one-line taste of what is inside, shown while closed. */
  preview?: ReactNode;
  defaultOpen?: boolean;
  children?: ReactNode;
}) {
  const empty = count === 0;
  return (
    <details className={s.relation} open={defaultOpen && !empty}>
      <summary className={s.relationSummary}>
        <span className={s.relationIcon}>
          <i className={`ti ${icon}`} aria-hidden />
        </span>
        <span className={s.relationText}>
          <span className={s.relationTitle}>{title}</span>
          <span className={s.relationPreview}>{empty ? 'None' : preview}</span>
        </span>
        <span className={s.relationCount}>{count}</span>
        {!empty && <i className={`ti ti-chevron-right ${s.relationChevron}`} aria-hidden />}
      </summary>
      {!empty && <div className={s.relationBody}>{children}</div>}
    </details>
  );
}

/* ─────────────────────────── Controls ─────────────────────────── */

export function Controls({ children, count }: { children: ReactNode; count?: ReactNode }) {
  return (
    <div className={s.controls}>
      {children}
      {count !== undefined && <span className={s.controlsCount}>{count}</span>}
    </div>
  );
}

export function SearchBox({
  value,
  onChange,
  placeholder = 'Search',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className={s.search}>
      <i className="ti ti-search" aria-hidden />
      <input
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function Select({
  value,
  onChange,
  options,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  label: string;
}) {
  return (
    <select className={s.select} value={value} aria-label={label} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** The prototype's `.seg` — a small set of mutually exclusive filters. */
export function Segmented({
  value,
  onChange,
  options,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  label: string;
}) {
  return (
    <div className={s.seg} role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`${s.segBtn} ${value === o.value ? s.segOn : ''}`}
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Button({
  onClick,
  icon,
  primary,
  accent,
  spinning,
  disabled,
  title,
  children,
}: {
  onClick?: () => void;
  icon?: string;
  /** Blue. */
  primary?: boolean;
  /** Yellow — the one primary action on a screen, as in the prototype's `.btn-y`. */
  accent?: boolean;
  /** Turn the icon while something is happening.
   *
   *  A disabled button says only that it cannot be pressed, which looks the same
   *  as broken. Forty seconds of that is a long time to wonder. */
  spinning?: boolean;
  disabled?: boolean;
  title?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`${s.btn} ${accent ? s.btnAccent : primary ? s.btnPrimary : ''}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {icon && <i className={`ti ${icon} ${spinning ? s.spin : ''}`} aria-hidden />}
      {children}
    </button>
  );
}

/* ─────────────────────────── Skeleton ─────────────────────────── */

export function Skeleton({ height = 16, width = '100%' }: { height?: number; width?: string }) {
  return <div className={s.skeleton} style={{ height, width }} />;
}

export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className={s.stackBox}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} height={20} />
      ))}
    </div>
  );
}
